// src/utils/money.js
// Centralized, crash-proof currency helpers for invoices/quotes/payments.
// Assumption: single-currency per company (NOT true multi-currency per document).

// Global defaults (used only when we cannot infer from company/profile)
const DEFAULT_LOCALE = 'en-CA';
const DEFAULT_CURRENCY = 'CAD'; // ✅ change once here if your global default differs

export function isIsoCurrency(code) {
  return /^[A-Za-z]{3}$/.test(String(code || '').trim());
}

export function safeCurrency(code, fallback = DEFAULT_CURRENCY) {
  const s = String(code || '').trim().toUpperCase();
  return isIsoCurrency(s) ? s : fallback;
}

export function safeLocale(locale, fallback = DEFAULT_LOCALE) {
  const s = String(locale || '').trim();
  return s || fallback;
}

/**
 * Best-effort company currency reader (frontend-only).
 * Reads cached /auth/profile from localStorage if available.
 */
export function getCompanyCurrency(fallback = DEFAULT_CURRENCY) {
  try {
    const raw = localStorage.getItem('profile');
    if (!raw) return safeCurrency(fallback);

    const p = JSON.parse(raw || '{}');

    // Support multiple profile shapes (you had variations across modules)
    const c =
      p?.company?.currency ||
      p?.companyCurrency ||
      p?.currency ||
      p?.company?.preferences?.currency ||
      p?.company?.settings?.currency;

    return safeCurrency(c, fallback);
  } catch {
    return safeCurrency(fallback);
  }
}

/**
 * Format a numeric value as money in a stable, locale-aware way.
 *
 * @param {number|string} value        Amount to format
 * @param {string}        currencyCode ISO 4217 code (e.g. 'CAD', 'USD')
 * @param {string}        locale       BCP 47 locale (e.g. 'en-CA')
 */
export function formatMoney(
  value,
  currencyCode = getCompanyCurrency(),
  locale = DEFAULT_LOCALE
) {
  const num = Number(value);
  const n = Number.isFinite(num) ? num : 0;
  const cur = safeCurrency(currencyCode);
  const loc = safeLocale(locale);

  try {
    return n.toLocaleString(loc, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    // last resort: never crash the UI
    try {
      return n.toLocaleString(loc, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return `${cur} ${n.toFixed(2)}`;
    }
  }
}

// Optional exports if you want consistent defaults elsewhere
export const MONEY_DEFAULTS = Object.freeze({
  locale: DEFAULT_LOCALE,
  currency: DEFAULT_CURRENCY,
});
